import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { emailRule, requireRule } from "src/components/common/forms/validation.ts";
import i18n from "i18next";
import { DocumentBaseFields } from "src/components/views/documents/revisions/types.ts";
import { DocumentSendingApi } from "src/api/generated/documents/api/documentSendingApi.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { FormRichTextEditor } from "src/components/common/forms/fields/FormRichTextEditor.tsx";
import { useOAuthAuthentication } from "src/utils/oAuth.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { SendDocumentsViewApi, SendDocumentsViewApi_InitData } from "src/api/generated/documents/api/sendDocumentsViewApi.ts";
import { AavoObjectRef } from "src/api/generated/common/aavoObject/aavoObjectRef.ts";
import { EmailTemplateSelectButton } from "src/components/views/email/EmailTemplateSelectButton.tsx";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { FormFromMailField } from "src/components/views/tenantMail/FormFromMailField.tsx";
import { useRecentFromMailboxes } from "src/components/views/tenantMail/useRecentFromMailboxes.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SendDocumentsViewProps {
	documents: DocumentBaseFields[];
	onCompletedSuccessfully: () => void;
	objectRef?: AavoObjectRef;
}

interface SendDocumentsViewFormValues {
	mailFrom: string;
	receiverEmail: string;
	title: string;
	message: string;
	zipFiles: boolean;
}

export const SendDocumentsView = ({ documents, onCompletedSuccessfully, objectRef }: SendDocumentsViewProps) => {
	const { checkAuthentication } = useOAuthAuthentication(DocumentSendingApi.ensureDocumentSendingAuthentication);
	const { supportsCustomFromAddress } = useGlobalInitData();
	const { currentUserEmail, recentFromMailboxes, defaultFromMailbox, rememberRecentMailbox, removeRecentMailbox } =
		useRecentFromMailboxes();

	const submitForm = async (values: SendDocumentsViewFormValues) => {
		const authResult = await checkAuthentication();
		if (authResult === "requiresAuthentication") {
			return "interrupted";
		}

		await DocumentSendingApi.sendDocuments({
			mailFrom: supportsCustomFromAddress ? values.mailFrom || undefined : undefined,
			mailReceiver: values.receiverEmail,
			title: values.title,
			message: values.message,
			sendAsZipFile: values.zipFiles,
			documentIds: documents.map((d) => d.documentId),
		});
		if (supportsCustomFromAddress) {
			rememberRecentMailbox(values.mailFrom);
		}
	};

	return (
		<AsyncForm<SendDocumentsViewApi_InitData, SendDocumentsViewFormValues, void>
			fetch={() =>
				SendDocumentsViewApi.getInitData({
					objectRef: objectRef,
				})
			}
			getDefaultValues={(data) => ({
				mailFrom: defaultFromMailbox,
				receiverEmail: data.defaultReceiverEmail,
				title: "",
				message: data.emailSignature ? `\n\n${data.emailSignature}` : "",
			})}
			submit={submitForm}
			submitLabel={i18n.t("send")}
			fitContent={true}
			columns={1}
			onCompleted={(result) => {
				if (result.type === "success") onCompletedSuccessfully();
			}}
			agentContext={{
				key: defineAgentUiContextKey("documents.sendDocuments.form"),
				title: i18n.t("send_by_email"),
				pathSegments: [i18n.t("form")],
				data: {
					documentIds: documents.map((document) => document.documentId),
					objectType: objectRef?.objectType ?? null,
					objectId: objectRef?.objectId ?? null,
				},
			}}
			render={({ control, setValue, data: {emailSignature} }) => (
				<>
					{supportsCustomFromAddress && (
						<FormFromMailField
							control={control}
							name={"mailFrom"}
							rules={emailRule()}
							currentUserEmail={currentUserEmail}
							recentMailboxes={recentFromMailboxes}
							onDeleteRecentMailbox={removeRecentMailbox}
						/>
					)}
					<FormTextField
						control={control}
						name={"receiverEmail"}
						rules={{
							...requireRule(),
							...emailRule(),
						}}
						tooltip={i18n.t("you_can_use_semicolon_to_separate_multiple_email_addresses")}
						label={i18n.t("receiver_mail")}
					/>
					<HorizontalBox sx={{ gap: 1, alignSelf: "stretch" }}>
						<FormTextField
							control={control}
							name={"title"}
							rules={requireRule()}
							label={i18n.t("title")}
							sx={{ flex: 1 }}
							autoFocus
						/>
						<EmailTemplateSelectButton
							onTemplateSelected={({ subject, body }) => {
								if (subject.trim() !== "") setValue("title", subject);
								setValue("message", body + (emailSignature ? `\n\n${emailSignature}` : ""));
							}}
						/>
					</HorizontalBox>
					<FormRichTextEditor
						control={control}
						name={"message"}
						sx={{
							flex: 1,
							gridColumn: "1 / -1",
						}}
					/>
					<FormCheckbox control={control} name={"zipFiles"} label={i18n.t("send_as_zip_file")} />
				</>
			)}
		/>
	);
};
